import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Alert, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "AI Voice Calls" list (`/app-voice-call`) — rows come from the bespoke
 * /api/voice/calls endpoints (routes/voiceCallsRouter.js), NOT generic CRUD: dialing
 * is a verb with side effects. New Call dialog collects the number, the purpose, the
 * desired-JSON goal fields, facts to deliver, and an optional schedule; the server
 * creates the audit row first and the voice worker dials.
 */
export function CallStatusBadge({ status }) {
  const variant = { completed: 'success', in_progress: 'primary', dialing: 'primary', pending: 'secondary', failed: 'error', no_answer: 'warning', busy: 'warning', canceled: 'secondary' };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status}</Badge>;
}

const FIELD_TYPES = ['string', 'number', 'boolean'];
const BLANK_FIELD = { name: '', type: 'string', description: '' };

/** Row-based builder for the desired-JSON collection schema — shared with the
 *  campaigns page. Emits {fields:[{name,type,description}]} or null when empty. */
export function GoalFieldsEditor({ fields, setFields }) {
  const setAt = (i, key, value) => setFields(fields.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">Information to collect (the desired JSON)</label>
      {fields.map((f, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] flex-1"
            placeholder="field_name (snake_case)"
            value={f.name}
            onChange={(e) => setAt(i, 'name', e.target.value)}
          />
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
            value={f.type}
            onChange={(e) => setAt(i, 'type', e.target.value)}
          >
            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] flex-1"
            placeholder="What to ask for"
            value={f.description}
            onChange={(e) => setAt(i, 'description', e.target.value)}
          />
          <button type="button" className="text-xs text-red-600" onClick={() => setFields(fields.filter((_, idx) => idx !== i))}>✕</button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => setFields([...fields, { ...BLANK_FIELD }])}>Add field</Button>
    </div>
  );
}

/** key:value textarea → object (one pair per line) for facts the agent should deliver. */
export function parseDeliverInfo(text) {
  const out = {};
  for (const line of String(text || '').split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return Object.keys(out).length ? out : null;
}

function NewCallDialog({ onClose, onCreated }) {
  const { fetchApi } = useAppApi();
  const [toNumber, setToNumber] = useState('');
  const [purpose, setPurpose] = useState('collect');
  const [fields, setFields] = useState([{ ...BLANK_FIELD }]);
  const [deliverText, setDeliverText] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  let submitLabel = scheduledAt ? 'Schedule call' : 'Call now';
  if (busy) submitLabel = 'Creating…';

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      const goalFields = fields.filter((f) => f.name.trim());
      await fetchApi('voiceCallCreate', {
        to_number: toNumber,
        purpose,
        goal_schema: (purpose !== 'deliver' && goalFields.length) ? { fields: goalFields } : null,
        deliver_payload: purpose !== 'collect' ? parseDeliverInfo(deliverText) : null,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      onCreated();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">New AI voice call</h2>
        <button className="text-sm text-secondary-500" onClick={onClose}>Close</button>
      </div>
      {err && <Alert variant="error" title="Could not create call">{err}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <Input label="Phone number (international)" placeholder="+15551234567" value={toNumber} onChange={(e) => setToNumber(e.target.value)} />
        <Select
          label="Purpose"
          options={[
            { value: 'collect', label: 'Collect information' },
            { value: 'deliver', label: 'Deliver information' },
            { value: 'both', label: 'Collect + deliver' },
          ]}
          value={purpose}
          onChange={(v) => setPurpose(v)}
        />
      </div>
      {purpose !== 'deliver' && <GoalFieldsEditor fields={fields} setFields={setFields} />}
      {purpose !== 'collect' && (
        <div>
          <label className="block text-sm font-medium mb-1">Information to deliver (key: value per line)</label>
          <textarea
            className="w-full min-h-[70px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
            placeholder={'order_status: shipped\neta: Tuesday'}
            value={deliverText}
            onChange={(e) => setDeliverText(e.target.value)}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <label className="block text-sm font-medium mb-1">Schedule (optional — empty dials now)</label>
          <input
            type="datetime-local"
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>
        <div className="text-right">
          <Button onClick={submit} disabled={busy || !toNumber.trim()}>{submitLabel}</Button>
        </div>
      </div>
      <p className="text-xs text-secondary-500">This rings a real phone via the company's ElevenLabs AI Call connection and spends provider credits.</p>
    </Card>
  );
}

/** Per-company compliance + cost settings and the do-not-call list — collapsed by
 *  default; edits require calls.configure (the same split as the webhook screen). */
function ComplianceSection() {
  const { fetchApi } = useAppApi();
  const [settings, setSettings] = useState(null);
  const [dnc, setDnc] = useState([]);
  const [newNumber, setNewNumber] = useState('');
  const [status, setStatus] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    Promise.all([
      fetchApi('voiceSettingsGet').catch(() => null),
      fetchApi('voiceDncList').catch(() => []),
    ]).then(([s, d]) => {
      if (s) setSettings(s);
      setDnc(Array.isArray(d) ? d : []);
    });
  };
  useEffect(load, []);

  const setField = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setErr('');
    setStatus('');
    try {
      const saved = await fetchApi('voiceSettingsPut', {
        quiet_hours_start: settings.quiet_hours_start || null,
        quiet_hours_end: settings.quiet_hours_end || null,
        timezone: settings.timezone || null,
        daily_call_cap: settings.daily_call_cap || null,
        monthly_credit_budget: settings.monthly_credit_budget || null,
        recording_disclosure: settings.recording_disclosure || null,
      });
      setSettings(saved);
      setStatus('Saved.');
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const addDnc = async () => {
    setErr('');
    try {
      await fetchApi('voiceDncAdd', { phone_number: newNumber });
      setNewNumber('');
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  const removeDnc = async (id) => {
    try {
      await fetchApi('voiceDncDelete', id);
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  if (!settings) return null;

  return (
    <details>
      <summary className="cursor-pointer font-semibold py-2">Compliance & cost settings — quiet hours, caps, do-not-call</summary>
      <Card className="p-3 space-y-3">
        {err && <Alert variant="error" title="Error">{err}</Alert>}
        <div className="grid grid-cols-3 gap-3">
          <Input label="Quiet hours start (HH:MM)" placeholder="21:00" value={settings.quiet_hours_start || ''} onChange={(e) => setField('quiet_hours_start', e.target.value)} />
          <Input label="Quiet hours end (HH:MM)" placeholder="08:00" value={settings.quiet_hours_end || ''} onChange={(e) => setField('quiet_hours_end', e.target.value)} />
          <Input label="Timezone (IANA)" placeholder="America/New_York" value={settings.timezone || ''} onChange={(e) => setField('timezone', e.target.value)} />
          <Input label="Daily call cap" type="number" value={settings.daily_call_cap ?? ''} onChange={(e) => setField('daily_call_cap', e.target.value)} />
          <Input label="Monthly credit budget" type="number" value={settings.monthly_credit_budget ?? ''} onChange={(e) => setField('monthly_credit_budget', e.target.value)} />
          <div className="text-sm self-end pb-1 text-secondary-500">
            Spent this month: {Number(settings.spend_credits_month || 0).toFixed(2)} credits
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Recording disclosure (spoken via the agent's {'{{recording_disclosure}}'} variable)</label>
          <textarea
            className="w-full min-h-[50px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm"
            value={settings.recording_disclosure || ''}
            onChange={(e) => setField('recording_disclosure', e.target.value)}
          />
        </div>
        <Can module="calls" action="configure">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save}>Save settings</Button>
            {status && <span className="text-xs text-emerald-600">{status}</span>}
          </div>
        </Can>

        <div className="pt-2 border-t border-[var(--app-border)]">
          <h3 className="font-semibold text-sm mb-2">Do-not-call list ({dnc.length})</h3>
          <Can module="calls" action="configure">
            <div className="flex gap-2 mb-2">
              <input
                className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)]"
                placeholder="+15551234567"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
              />
              <Button size="sm" variant="secondary" onClick={addDnc} disabled={!newNumber.trim()}>Add</Button>
            </div>
          </Can>
          {dnc.length === 0 && <p className="text-xs text-secondary-500">No suppressed numbers. Callers who say "stop calling me" are added automatically.</p>}
          <div className="space-y-1">
            {dnc.map((row) => (
              <div key={row.id} className="flex items-center justify-between text-sm border-t border-[var(--app-border)] py-1">
                <span className="font-mono">{row.phone_number}</span>
                <span className="text-xs text-secondary-500">{row.source}{row.reason ? ` — ${row.reason}` : ''}</span>
                <Can module="calls" action="configure">
                  <button className="text-xs text-red-600 hover:underline" onClick={() => removeDnc(row.id)}>Remove</button>
                </Can>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </details>
  );
}

const CALL_STATUSES = ['pending', 'dialing', 'in_progress', 'completed', 'failed', 'no_answer', 'busy', 'canceled'];
const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  ...CALL_STATUSES.map((s) => ({ label: s, value: s })),
];

const CALL_COLUMNS = [
  { key: 'to_number', label: 'To', render: (c) => <span className="font-medium text-primary-600">{c.to_number}</span> },
  { key: 'purpose', label: 'Purpose' },
  { key: 'status', label: 'Status', searchable: false, render: (c) => <CallStatusBadge status={c.status} /> },
  {
    key: 'transcript_summary',
    label: 'Result',
    render: (c) => (
      <span className="block max-w-[280px] truncate" title={c.transcript_summary || ''}>
        {c.transcript_summary || (c.error || '—')}
      </span>
    ),
  },
  { key: 'duration_secs', label: 'Duration', searchable: false, render: (c) => (c.duration_secs ? `${c.duration_secs}s` : '—') },
  { key: 'created_at', label: 'Created', searchable: false, render: (c) => <span className="text-secondary-500">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span> },
];

export default function VoiceCallsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [calls, setCalls] = useState([]);
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const load = () => {
    fetchApi('voiceCallList', status ? { status } : {})
      .then((rows) => setCalls(Array.isArray(rows) ? rows : []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    load();
    const timer = setInterval(load, 10000); // live-ish status while calls are on the phone
    return () => clearInterval(timer);
  }, [status]);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const filteredCalls = applicationId
    ? calls.filter(c => c.application_id == null || c.application_id === Number(applicationId))
    : calls;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="AI Voice Calls"
        subtitle={<>Outbound AI phone calls — transcripts, recordings, and collected data are stored for audit.</>}
        actions={(
          <Can module="calls" action="dial">
            <Button size="sm" onClick={() => setShowNew((v) => !v)}>New call</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Could not load">{err}</Alert>}
      {showNew && <NewCallDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      <ComplianceSection />

      {/* Client-side over the endpoint's own window (GET /api/voice/calls caps at 200):
          status is filtered server-side via the toolbar, but serverSide paging is not an
          option — there is no count endpoint, so totalRecords can't be supplied. */}
      <DataTable
        data={filteredCalls}
        columns={CALL_COLUMNS}
        loading={loading}
        emptyText='No calls yet — place one with "New call".'
        search={{ placeholder: 'Search calls…', keys: ['to_number', 'purpose', 'transcript_summary'] }}
        toolbar={(
          <>
            <div className="w-48 sm:w-56">
              <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
            </div>
            <div className="w-48">
              <Select options={STATUS_OPTIONS} value={status} onChange={setStatus} placeholder="All statuses" />
            </div>
          </>
        )}
        onRowClick={(call) => nav(`/app-voice-call/${call.id}`)}
        onReload={load}
      />
    </PageShell>
  );
}
